// The stage: the count as one huge numeral, the phrase bar under it, the clave row under that. The whole stage is the tap target.
//
// The numeral, the phrase bar's active cell and the clave row's lit dot are written straight to the DOM from the shared animation-frame
// loop (frames.ts); Preact renders only what changes on events (prompts, status, the dancer's On1/On2 setting, the clave setting).

import { CLAVE } from '@salsacount/core';
import { useEffect, useRef } from 'preact/hooks';
import type { ClaveSetting, EngineState } from '../engine/engine.js';
import { replayAllTaps, setClave, tapNoteWords, tapStage, tapWords } from './actions.js';
import { Choice } from './controls.js';
import { useEngineState } from './engine-state.js';
import { onFrame } from './frames.js';
import { SongEndLine } from './sponsor-ui.js';
import { Toast } from './toast.js';
import { announce, hasKeyboard, ui } from './ui.js';

const PULSE_S = 0.12;
const flag = (on: boolean): '' | undefined => (on ? '' : undefined);

interface Prompt {
  main: string;
  /** Smaller lines under the main one (DESIGN §2, §6). */
  subs?: readonly string[];
  /** Why the earlier taps were set aside, in one sentence; it stays until the next tap (DESIGN §9). */
  note?: string;
}

/** What the stage says instead of a count (DESIGN §2, §3 and §6). */
function promptFor(s: EngineState): Prompt | null {
  if (s.source === 'lab') return null;
  if (s.status === 'decoding') return { main: 'Opening the song' };
  if (s.status === 'error') return { main: s.error ?? 'Something went wrong. Try another song.' };
  if (s.truthMode) return { main: 'Truth mode', subs: [`Tap on every 1. The count is hidden. ${s.truthTaps} ${s.truthTaps === 1 ? 'tap' : 'taps'} so far.`] };
  if (s.status === 'needs-taps') {
    if (s.taps === 0 && s.auto.run === 'finding') return { main: 'Finding the count…' };
    if (s.taps > 0) return s.tapNote ? { main: 'Got it. Tap again on the next 1.', note: tapNoteWords(s.tapNote.why) } : { main: 'Got it. Tap again on the next 1.' };
    const subs = ['Once per count of eight, not on every beat.'];
    if (hasKeyboard()) subs.push('Click, or press T.');
    if (!s.playing) subs.push('The song starts when you tap.');
    return { main: 'Tap when you feel 1', subs };
  }
  return null;
}

function statusLine(s: EngineState): string {
  if (s.replay) return `Replaying tap ${s.replay.tap} of ${s.replay.of}`;
  if (s.source === 'lab') return `Rhythm Lab${s.tempoBpm ? ` · ${Math.round(s.tempoBpm)} BPM` : ''}`;
  const bpm = s.tempoBpm ? ` · ${Math.round(s.tempoBpm)} BPM` : '';
  if (s.status === 'decoding') return 'Decoding…';
  if (s.status === 'needs-taps') return s.taps === 0 && s.auto.run === 'finding' ? '' : 'Needs taps'; // the prompt says it
  if (s.status === 'provisional') return s.auto.origin === 'auto' || s.auto.origin === 'overridden' ? `Found automatically${bpm}` : `Counting from your taps${bpm}`;
  if (s.status === 'locked') return s.auto.origin === 'overridden' ? `Count set by you${bpm}` : `✓ Locked${bpm}`;
  return '';
}

export function Stage() {
  const s = useEngineState();
  const { on, countNote, toast, big } = ui.use();
  const stage = useRef<HTMLElement>(null);
  const numeral = useRef<HTMLDivElement>(null);
  const phrase = useRef<HTMLDivElement>(null);
  const clave = useRef<HTMLDivElement>(null);
  const speed = useRef(s.speed);
  speed.current = s.speed;

  useEffect(() => {
    const cells = phrase.current!.querySelectorAll<HTMLElement>('.cell');
    const slots = clave.current!.querySelectorAll<HTMLElement>('[data-slot]');
    const numeralEl = numeral.current!;
    const stageEl = stage.current!;
    let count = -1;
    let hidden = false;
    let pulse = -1;
    let slot = -2;
    return onFrame((f) => {
      const shown = !f.hidden && f.count > 0 ? f.count : 0;
      if (f.hidden !== hidden) {
        hidden = f.hidden;
        if (hidden) stageEl.dataset.hidden = ''; else delete stageEl.dataset.hidden;
      }
      if (shown !== count) {
        if (count > 0) cells[count - 1]?.removeAttribute('data-active');
        if (shown > 0) cells[shown - 1]?.setAttribute('data-active', '');
        numeralEl.textContent = shown ? String(shown) : '';
        numeralEl.dataset.count = String(shown);
        count = shown;
      }
      // The pulse: 1 at the instant of the beat, back to 0 in 120 ms (ease-out); real time, so it follows the speed.
      let p = 0;
      if (shown && f.playing && f.bpm > 0) {
        const t = (f.beatPhase * 60) / f.bpm / speed.current / PULSE_S;
        if (t < 1) p = (1 - t) ** 3;
      }
      p = Math.round(p * 50) / 50;
      if (p !== pulse) {
        numeralEl.style.setProperty('--pulse', String(p));
        pulse = p;
      }
      // The clave row: the half-beat slot the music is in.
      const now = shown ? Math.min(15, Math.floor(f.phrasePosition * 2)) : -1;
      if (now !== slot) {
        if (slot >= 0) slots[slot]?.removeAttribute('data-now');
        if (now >= 0) slots[now]?.setAttribute('data-now', '');
        slot = now;
      }
    });
  }, []);

  // The live region says why a tap started again (not on first render: a note that is already there was said when it was made).
  const spoken = useRef(s.tapNote?.seq ?? 0);
  useEffect(() => {
    const seq = s.tapNote?.seq ?? 0;
    if (s.tapNote && seq !== spoken.current) announce(tapNoteWords(s.tapNote.why));
    spoken.current = seq;
  }, [s.tapNote]);

  const prompt = promptFor(s);
  const lab = s.source === 'lab';
  const setting: ClaveSetting = lab ? (s.lab?.clave ?? 'unknown') : s.clave.setting;
  const hits = new Set(setting === 'unknown' ? [] : CLAVE[setting].map((p) => Math.round((p - 1) * 2)));
  const emphasised = on === 1 ? [1, 5] : [2, 6];
  const evidence = s.clave.evidence?.direction;
  // A count the proposal gave names a direction only when the model did (DESIGN §11, 6); a count from taps keeps what the music says (the evidence for the map in force).
  const fromAuto = s.auto.origin === 'auto';
  const named = fromAuto ? s.auto.direction : evidence && evidence !== 'none' ? evidence : null;
  const heard = !lab && named ? `Sounds like ${named}` : null;
  const tapName = lab ? (s.playing ? 'Pause the band' : 'Start the band') : 'Tap on 1';

  return (
    <section class="stage" ref={stage} aria-label="Count" data-status={s.status} data-source={s.source} data-truth={flag(s.truthMode)}>
      <button
        type="button"
        class="tap-target"
        aria-label={tapName}
        onPointerDown={(e) => { if (e.button === 0) { e.preventDefault(); tapStage(); } }}
        onClick={(e) => { if (e.detail === 0) tapStage(); }} /* the keyboard; a pointer tap was taken on pointerdown */
      />
      {s.tapSeq > 0 && <span key={s.tapSeq} class="tap-ack" data-n={s.tapSeq} data-testid="tap-ack" aria-hidden="true" />}
      <Toast />
      {s.ended && !s.playing && !s.replay && !big && s.source === 'file' ? (
        <SongEndLine />
      ) : countNote && countNote.track === s.track?.id && !toast && !s.replay && s.status !== 'needs-taps' ? (
        <div class="status-line stage-extra" data-note="" data-testid="count-note">
          <span>Count set from {tapWords(countNote.taps)}</span>
          <button type="button" class="btn quiet" onClick={replayAllTaps}>Replay</button>
        </div>
      ) : (
        <div class="status-line stage-extra" data-testid="status-line" data-replay={s.replay ? '' : undefined}>{statusLine(s)}</div>
      )}
      <div class="numeral-box">
        <div class="numeral" ref={numeral} aria-hidden="true" data-count="0" />
        {prompt && (
          <div class="prompt" data-error={flag(s.status === 'error')} role={s.status === 'error' ? 'alert' : undefined}>
            <div class="prompt-main">{prompt.main}</div>
            {prompt.note && <div class="prompt-note" data-testid="tap-note">{prompt.note}</div>}
            {prompt.subs?.map((line, i) => <div key={i} class="prompt-sub">{line}</div>)}
            {s.status === 'decoding' && s.progress && (
              <progress class="progress" max={1} value={s.progress.fraction} aria-label="Opening the song" />
            )}
          </div>
        )}
      </div>
      <div class="phrase" ref={phrase} aria-hidden="true">
        {Array.from({ length: 8 }, (_, i) => {
          const n = i + 1;
          return <span key={n} class="cell" data-n={n} data-hollow={flag(n === 4 || n === 8)} data-emph={flag(emphasised.includes(n))} />;
        })}
      </div>
      <div class="clave-row stage-extra">
        <div class="clave" ref={clave} aria-hidden="true">
          {Array.from({ length: 8 }, (_, i) => (
            <span key={i} class="clave-col">
              <i class="dot" data-slot={2 * i} data-hit={flag(hits.has(2 * i))} />
              {i < 7 && <i class="dot and" data-slot={2 * i + 1} data-hit={flag(hits.has(2 * i + 1))} />}
            </span>
          ))}
        </div>
        <div class="clave-controls">
          <span class="clave-label" id="clave-label">Clave</span>
          <Choice<'3-2' | '2-3'>
            label="Clave direction"
            value={setting === 'unknown' ? null : setting}
            options={[{ value: '3-2', label: '3-2' }, { value: '2-3', label: '2-3' }]}
            disabled={!lab && (s.status === 'needs-taps' || s.status === 'decoding' || s.status === 'error' || s.truthMode)}
            onChange={(v) => setClave(v)}
          />
          {heard && <span class="clave-heard">{heard}</span>}
        </div>
      </div>
    </section>
  );
}
